<template>
  <div class="report-page">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <h2 class="page-title"><i class="el-icon-s-data"></i> 实时报表</h2>
    </div>

    <!-- 报表类型选择 - 卡片式Tab -->
    <div class="report-tabs">
      <div v-for="group in reportGroups" :key="group.label" class="tab-group"
        :class="{ active: activeGroup === group.label }" @click="activeGroup = group.label">
        <i :class="group.icon"></i>
        <span>{{ group.label }}</span>
      </div>
    </div>

    <div class="report-subtabs" v-if="activeGroup">
      <div v-for="item in activeItems" :key="item.key" class="subtab-item"
        :class="{ active: currentReport.key === item.key }" @click="selectReport(item)">
        <i :class="item.icon"></i>{{ item.name }}
      </div>
    </div>

    <!-- 筛选区 -->
    <div class="filter-bar" v-if="currentReport.key">
      <div class="filter-row">
        <div class="filter-item filter-fee">
          <el-radio-group v-model="queryForm.filterMode" size="mini" style="margin-bottom:4px">
            <el-radio-button label="type">按费用项目</el-radio-button>
            <el-radio-button label="name">按费用名称</el-radio-button>
          </el-radio-group>
          <el-popover v-if="queryForm.filterMode==='type'" placement="bottom-start" width="260" trigger="click" popper-class="fee-popover">
            <div class="fee-check-header">
              <el-checkbox :indeterminate="feeCheckIndeterminate" v-model="feeCheckAll">全选 / 取消</el-checkbox>
            </div>
            <el-checkbox-group v-model="queryForm.feeTypeCds" class="fee-check-group">
              <el-checkbox v-for="f in feeTypeList" :key="f.statusCd" :label="f.statusCd">{{ f.name }}</el-checkbox>
            </el-checkbox-group>
            <el-button slot="reference" size="small" class="fee-btn">
              选择费用项目 <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
          </el-popover>
          <el-popover v-else placement="bottom-start" width="280" trigger="click" popper-class="fee-popover">
            <div class="fee-check-header">
              <el-checkbox :indeterminate="feeNameIndeterminate" v-model="feeNameAll" @change="onFeeNameAll">全选 / 取消</el-checkbox>
            </div>
            <el-checkbox-group v-model="queryForm.feeConfigIds" class="fee-check-group">
              <el-checkbox v-for="f in feeNameList" :key="f.configId" :label="f.configId">{{ f.feeName }}</el-checkbox>
            </el-checkbox-group>
            <el-button slot="reference" size="small" class="fee-btn">
              选择费用名称 <i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
          </el-popover>
        </div>
        <div class="filter-item filter-date">
          <label>时间范围</label>
          <el-date-picker v-model="queryForm.dateRange" type="monthrange" value-format="yyyy-MM"
            range-separator="~" start-placeholder="开始月" end-placeholder="结束月" size="small" />
        </div>
        <div class="filter-item filter-actions">
          <el-button type="primary" size="small" icon="el-icon-search" @click="generateReport" :loading="loading">生成</el-button>
          <el-button type="success" size="small" icon="el-icon-download" @click="exportReport" :loading="exporting" :disabled="!tableData.length">导出</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="resetForm">重置</el-button>
        </div>
      </div>
    </div>

    <!-- 结果区 -->
    <div class="result-area" v-if="tableHeaders.length">
      <div class="result-title">
        <span>{{ currentReport.name }}（{{ queryForm.dateRange[0] }} ~ {{ queryForm.dateRange[1] }}）</span>
        <span class="result-count">共 {{ tableData.length }} 条</span>
      </div>
      <el-table :data="tableData" border stripe size="small" max-height="500"
        :row-class-name="rowClass">
        <el-table-column v-for="(col, idx) in tableHeaders" :key="idx"
          :prop="col.prop" :label="col.label" :min-width="col.width || 90"
          :align="col.align || 'center'" :fixed="col.fixed" />
      </el-table>
    </div>

    <!-- 空状态 -->
    <div v-if="!currentReport.key" class="empty-state">
      <i class="el-icon-s-data"></i>
      <p>请选择上方报表类型开始</p>
    </div>
  </div>
</template>

<script>
import { getCommunityId, getDict } from '@/api/community/communityApi'
import { generateRealTimeReport } from '@/api/report/realTimeReportApi'
import request from '@/utils/request'

const REPORT_GROUPS = [
  { label: '欠费', icon: 'el-icon-warning-outline', key: 'unpaid',
    items: [
      { key: 'report_unpaid_summary', name: '欠收统计表', icon: 'el-icon-pie-chart' },
      { key: 'report_unpaid_detail', name: '欠收月份明细表', icon: 'el-icon-tickets' },
      { key: 'report_arrears_monthly', name: '欠费月报表', icon: 'el-icon-document' }
    ]},
  { label: '已收', icon: 'el-icon-circle-check', key: 'paid',
    items: [
      { key: 'report_paid_detail', name: '已收月份明细表', icon: 'el-icon-tickets' },
      { key: 'report_paid_summary', name: '已收统计表', icon: 'el-icon-pie-chart' }
    ]},
  { label: '应收', icon: 'el-icon-document', key: 'receivable',
    items: [
      { key: 'report_receivable_summary', name: '应收统计表', icon: 'el-icon-pie-chart' }
    ]},
  { label: '收款', icon: 'el-icon-money', key: 'collection',
    items: [
      { key: 'report_collection_rate', name: '收费率分析表', icon: 'el-icon-data-board' }
    ]}
]

export default {
  name: 'RealTimeReport',
  data() {
    const now = new Date()
    return {
      reportGroups: REPORT_GROUPS,
      activeGroup: '',
      currentReport: {},
      queryForm: {
        reportType: '',
        filterMode: 'type',
        feeTypeCds: [],
        feeConfigIds: [],
        dateRange: [`${now.getFullYear()}-01`, `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`]
      },
      feeTypeList: [],
      feeNameList: [],
      tableData: [],
      tableHeaders: [],
      loading: false,
      exporting: false
    }
  },
  computed: {
    activeItems() {
      const g = this.reportGroups.find(r => r.label === this.activeGroup)
      return g ? g.items : []
    },
    feeCheckAll: {
      get() { return this.feeTypeList.length > 0 && this.queryForm.feeTypeCds.length === this.feeTypeList.length },
      set(v) { if (v) { this.queryForm.feeTypeCds = this.feeTypeList.map(f => f.statusCd) } else { this.queryForm.feeTypeCds = [] } }
    },
    feeCheckIndeterminate() {
      const c = this.queryForm.feeTypeCds.length
      return c > 0 && c < this.feeTypeList.length
    },
    feeNameIndeterminate() {
      const c = this.queryForm.feeConfigIds.length
      return c > 0 && c < this.feeNameList.length
    },
    feeNameAll: {
      get() { return this.feeNameList.length > 0 && this.queryForm.feeConfigIds.length === this.feeNameList.length },
      set(v) { if (v) { this.queryForm.feeConfigIds = this.feeNameList.map(f => f.configId) } else { this.queryForm.feeConfigIds = [] } }
    }
  },
  created() {
    this.loadFeeTypes()
  },
  methods: {
    async loadFeeTypes() {
      try {
        this.feeTypeList = await getDict('pay_fee_config', 'fee_type_cd')
        const resp = await request({ url: '/feeConfig.listFeeConfigs', method: 'get', params: { page: 1, row: 999, communityId: getCommunityId() } })
        const result = resp.data || resp
        const list = result.data || result
        this.feeNameList = (Array.isArray(list) ? list : (list.feeConfigs || list.feeConfigVos || [])).map(f => ({ configId: f.configId, feeName: f.feeName }))
      } catch (e) { /* ignore */ }
    },
    onFeeCheckAll(v) {
      this.queryForm.feeTypeCds = v ? this.feeTypeList.map(f => f.statusCd) : []
    },
    selectReport(item) {
      this.currentReport = { ...item, name: item.name }
      this.queryForm.reportType = item.key
      this.tableData = []
      this.tableHeaders = []
    },
    async generateReport() {
      if (!this.queryForm.reportType) return this.$message.warning('请选择报表类型')
      if (!this.queryForm.dateRange || this.queryForm.dateRange.length !== 2) return this.$message.warning('请选择时间范围')
      this.loading = true
      try {
        const res = await generateRealTimeReport({
          reportType: this.queryForm.reportType,
          communityId: getCommunityId(),
          startMonth: this.queryForm.dateRange[0],
          endMonth: this.queryForm.dateRange[1],
          filterMode: this.queryForm.filterMode,
          feeTypeCds: this.queryForm.feeTypeCds || [],
          feeConfigIds: this.queryForm.feeConfigIds || []
        })
        this.tableHeaders = res.headers || []
        this.tableData = res.rows || []
        this.$message.success(`生成完成，共 ${this.tableData.length} 条`)
      } catch (e) {
        this.$message.error(e.message || '生成失败')
      } finally { this.loading = false }
    },
    async exportReport() {
      if (!this.tableData.length) return this.$message.warning('无数据')
      this.exporting = true
      try {
        const h = this.tableHeaders
        let csv = '\uFEFF' + h.map(c => c.label).join(',') + '\n'
        this.tableData.forEach(r => {
          csv += h.map(c => (r[c.prop] != null ? String(r[c.prop]) : '')).join(',') + '\n'
        })
        const b = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const u = URL.createObjectURL(b); const a = document.createElement('a')
        a.href = u; a.download = `${this.currentReport.name}_${this.queryForm.dateRange[0]}_${this.queryForm.dateRange[1]}.csv`
        a.click(); URL.revokeObjectURL(u)
        this.$message.success('导出成功')
      } catch (e) { this.$message.error('导出失败') }
      finally { this.exporting = false }
    },
    resetForm() {
      this.queryForm.feeTypeCds = []
      this.queryForm.feeConfigIds = []
      const y = new Date().getFullYear()
      this.queryForm.dateRange = [`${y}-01`, `${y}-${String(new Date().getMonth()+1).padStart(2,'0')}`]
    },
    rowClass({ row }) {
      if (row.objName && (row.objName.indexOf('小计') !== -1)) return 'subtotal-row'
      if (row.feeName === '总计：') return 'grand-total-row'
      if (row.feeName === '合计：') return 'grand-total-row'
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.report-page {
  padding: 20px 24px;
  background: #f5f7fa;
  min-height: calc(100vh - 80px);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}
.page-header {
  margin-bottom: 20px;
  .page-title {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0 0 4px 0;
    i { color: #409EFF; margin-right: 6px; }
  }
  .page-subtitle { font-size: 13px; color: #909399; }
}

.report-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  .tab-group {
    padding: 8px 20px;
    background: #fff;
    border-radius: 8px 8px 0 0;
    cursor: pointer;
    font-size: 14px;
    color: #606266;
    border: 1px solid #e4e7ed;
    border-bottom: none;
    transition: all .2s;
    i { margin-right: 6px; }
    &:hover { color: #409EFF; }
    &.active { background: #409EFF; color: #fff; border-color: #409EFF; }
  }
}

.report-subtabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 0 8px 8px 8px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  .subtab-item {
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 13px;
    color: #606266;
    cursor: pointer;
    background: #f5f7fa;
    transition: all .2s;
    i { margin-right: 4px; font-size: 12px; }
    &:hover { background: #ecf5ff; color: #409EFF; }
    &.active { background: #409EFF; color: #fff; }
  }
}

.filter-bar {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.filter-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}
.filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  label { font-size: 12px; color: #909399; font-weight: 500; }
}
.filter-fee { flex: 1; min-width: 200px; }
.filter-date { min-width: 240px; }
.fee-count { color: #409EFF; font-weight: 600; }
.fee-btn { width: 100%; text-align: left; }
.fee-check-header { padding: 0 0 8px 0; border-bottom: 1px solid #ebeef5; margin-bottom: 8px; }
.fee-check-group { max-height: 280px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; }
.filter-actions {
  flex-direction: row;
  gap: 8px;
  align-items: flex-end;
  padding-bottom: 1px;
}
.filter-item .el-select, .filter-item .el-date-picker { width: 100%; }

.result-area {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
  overflow: hidden;
}
.result-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
}
.result-count { font-size: 12px; color: #67C23A; font-weight: normal; }

.empty-state {
  text-align: center;
  padding: 80px 0;
  color: #c0c4cc;
  i { font-size: 60px; }
  p { margin-top: 12px; font-size: 15px; color: #909399; }
}

/* 合计行高亮 */
:deep(.subtotal-row) { background: #fdf6ec; td { font-weight: 600; } }
:deep(.grand-total-row) { background: #ecf5ff; td { font-weight: 700; color: #303133; font-size: 13px; } }
</style>
<style lang="scss">
.fee-popover { padding: 10px 12px; }
</style>
